/**
 * pharmacy.ts — the pharmacy dispensing-lane gateway calls (PHARMACY session).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: pharmacy dispensing lane
 * (2026-07-29).
 *
 * WHY its own module rather than more functions in `orders.ts`: every call here
 * rides the PHARMACY session (pharmacy/session.ts), not the patient/staff one,
 * and every response is money-free by construction. Keeping them together means
 * the bearer choice is made in exactly one place — a future call added to
 * `orders.ts` cannot accidentally reach a pharmacy route with the wrong token,
 * and the money-free posture is a property of the file, not of each caller's
 * memory.
 *
 * ROUTING: the gateway maps `/api/v1/orders/*` onto the orders-service, which
 * mounts the dispensing boundary at `v1/pharmacy` — so the portal path for
 * orders-service `GET /v1/pharmacy/queue` is `/v1/orders/pharmacy/queue`. The
 * sign-in is an IDENTITY route (`/v1/identity/auth/pharmacy`): it is where
 * sessions are minted, and it is the only call here that carries no bearer.
 *
 * R41: every route below is `@Roles('PHARMACY')` server-side and scoped to the
 * pharmacy organisation on the VERIFIED session — never to anything the browser
 * sends. Nothing in this file is a control; it is a client.
 *
 * [BACKEND GAP — verified against the pharmacy-lane branches on 2026-07-29]
 * `pharmacyLogin` (identity `feat/pharmacy-auth`) and the queue / dispense /
 * reject / tracking / prescription routes (orders
 * `feat/pharmacy-scope-release-recovery`) all exist and this module matches
 * them exactly. TWO reads do NOT exist yet and are specified here against the
 * pinned contracts shapes so the console is complete the day they land:
 *
 *   - `getPharmacyOrder`     orders deferred `GET /v1/pharmacy/orders/:id`
 *                            explicitly — it needs the patient name/DOB and a
 *                            dispensing clinical extract, neither of which
 *                            orders-service holds today (no MPI linkage).
 *   - `getPharmacyActivity`  no endpoint on either service yet.
 *
 * Both callers render an honest "not available yet" state rather than a red
 * error, and neither blocks the dispense/reject/dispatch flow. Flagged in the
 * PR rather than papered over.
 */
import { ApiError, request } from './client';
import { GATEWAY_URL } from '../config';
import { getPharmacyToken, notifyPharmacyUnauthorized } from '../pharmacy/session';
import type {
  PharmacyActivityResponse,
  PharmacyAuthRequest,
  PharmacyAuthResult,
  PharmacyDispenseRequest,
  PharmacyOrderDetail,
  PharmacyQueueResponse,
  PharmacyQueueTab,
  PharmacyRejectRequest,
  PharmacyTrackingRequest,
  PharmacyWriteResult,
  PrescriptionDownloadGrant,
} from './types';

/**
 * One authed pharmacy call: attach the pharmacy bearer, and on a 401 drop the
 * pharmacy session so the console falls back to /auth-pharmacy.
 *
 * The shared client deliberately does NOT own this 401 (see its `bearer`
 * option): a dead pharmacy session must never clear a patient's session or
 * navigate a patient tab.
 */
async function pharmacyCall<T>(
  path: string,
  options: { method?: 'GET' | 'POST'; body?: unknown; parseError?: boolean } = {},
): Promise<T> {
  const token = getPharmacyToken();
  try {
    return await request<T>(path, {
      method: options.method ?? 'GET',
      body: options.body,
      // Per call, never a lane default: a body is read only where a screen has a
      // sentence to show from it — today that is `dispenseOrder` alone.
      parseError: options.parseError,
      // An absent token still makes the call: the SERVER decides, and its 401
      // is what drives the console back to sign-in. Short-circuiting here would
      // make the UI the gate, which R41 forbids.
      bearer: token ?? undefined,
    });
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) notifyPharmacyUnauthorized();
    throw error;
  }
}

/**
 * POST /v1/identity/auth/pharmacy — sign in with BOTH factors in one request.
 *
 * `parseError` is NOT set: the caller shows ONE uniform failure message
 * whatever comes back, so parsing a body could only tempt a future change into
 * leaking which factor failed. The link token never rides in a query string —
 * see PharmacyAuthPage for why.
 */
export function pharmacyLogin(body: PharmacyAuthRequest): Promise<PharmacyAuthResult> {
  return request('/v1/identity/auth/pharmacy', { method: 'POST', body });
}

/**
 * GET /v1/orders/pharmacy/queue?tab= → this pharmacy's assigned orders for one tab.
 *
 * `includeTest` is the "Show test orders" advanced filter. It is sent ONLY when
 * on, so the default request is byte-for-byte the one this console has always
 * made — and the server's default is the same either way, because R41 means the
 * absence of the flag is what decides, not this function.
 *
 * ⚠️ IT IS NOT A CONTROL. Turning it on cannot show this pharmacy anything
 * outside its own organisation — scope comes from the session server-side — and
 * it cannot make a flagged order dispensable. Both refusals live in
 * orders-service; this is a request for a view.
 */
export function getPharmacyQueue(tab: PharmacyQueueTab, includeTest = false): Promise<PharmacyQueueResponse> {
  const query = new URLSearchParams({ tab });
  if (includeTest) query.set('includeTest', 'true');
  return pharmacyCall(`/v1/orders/pharmacy/queue?${query.toString()}`);
}

/**
 * GET /v1/orders/pharmacy/orders/:id → the dispensing view (money-free,
 * minimised clinical extract).
 *
 * [BACKEND GAP] Deferred by orders-service — see the module doc.
 */
export function getPharmacyOrder(orderCaseId: string, includeTest = false): Promise<PharmacyOrderDetail> {
  // The detail read takes the SAME opt-in as the queue, and the drawer passes
  // through whatever the queue was showing when the row was clicked. Without
  // that, a revealed test row would open onto a 404 — a filter that shows you
  // something you cannot then open is worse than no filter.
  const suffix = includeTest ? '?includeTest=true' : '';
  return pharmacyCall(`/v1/orders/pharmacy/orders/${encodeURIComponent(orderCaseId)}${suffix}`);
}

/**
 * POST /v1/orders/pharmacy/orders/:id/dispense — confirm the dispense.
 *
 * The body carries `confirmed: true`, the step-up code (re-proving the
 * individual at the moment attribution is recorded) and an `idempotencyKey` the
 * server claims transactionally, so a double-click or a retry can never dispense
 * twice.
 *
 * STATUS CONTRACT — a WRONG STEP-UP CODE MUST BE 403, NEVER 401. Everywhere
 * else in this module a 401 means "your session is dead" and signs the
 * pharmacist out, which is right. On this one route the code is re-checked, so
 * answering 401 for a mistyped code would eject a pharmacist mid-shift over a
 * typo and send them back to their email for a fresh link. 403 keeps the
 * meaning honest: authenticated, but this action is refused.
 *
 * WHO CHECKS IT (2026-09-11). orders-service asks identity-service — the only
 * holder of the code's hash — before it records the dispense; until then the
 * code's shape was validated and its value ignored. A wrong code, a locked or a
 * withdrawn pharmacist are all the SAME 403 (never a hint which), and wrong
 * codes count towards the same lockout as sign-in. If the check cannot run the
 * answer is a 503 and nothing is dispensed — callers must not describe that as
 * a wrong code.
 *
 * `parseError` IS SET HERE, AND ONLY HERE IN THIS MODULE (2026-09-11). Two of
 * this route's answers are refusals no retry can fix, and the pharmacist needs
 * the reason: a 409 (`PHARMACY_TEST_RECORD_REFUSAL` — the clinic marked the
 * order as a test record) and a 400 ("This order is not awaiting dispense" — it
 * moved on, e.g. cancelled at another workstation). Both are Nest `{message}`
 * strings orders-service writes itself with no patient data in them (the
 * test-record one leaves the admin's reason out on purpose, R19), so they
 * surface as `ApiError.detail`. orders' schema 400 is `{formErrors,
 * fieldErrors}` with no `message`, so it parses to no `detail` at all.
 * `parseError` is per call, not per status, so the 403 and 503 bodies land in
 * `detail` too — and the dialog reads NEITHER: the refused-code copy stays
 * uniform so it is never an oracle, and a check that could not run keeps the
 * house wording.
 */
export function dispenseOrder(orderCaseId: string, body: PharmacyDispenseRequest): Promise<PharmacyWriteResult> {
  return pharmacyCall(`/v1/orders/pharmacy/orders/${encodeURIComponent(orderCaseId)}/dispense`, {
    method: 'POST',
    body,
    parseError: true,
  });
}

/**
 * POST /v1/orders/pharmacy/orders/:id/reject — refuse to dispense.
 *
 * The coarse `reason` is a closed vocabulary; the free-text `detail` reaches the
 * ADMIN team and the audit row ONLY. The patient is never told a pharmacy
 * refused their order.
 */
export function rejectDispense(orderCaseId: string, body: PharmacyRejectRequest): Promise<PharmacyWriteResult> {
  return pharmacyCall(`/v1/orders/pharmacy/orders/${encodeURIComponent(orderCaseId)}/reject`, {
    method: 'POST',
    body,
  });
}

/** POST /v1/orders/pharmacy/orders/:id/tracking — courier + tracking; moves the case to `dispatched`. */
export function recordTracking(orderCaseId: string, body: PharmacyTrackingRequest): Promise<PharmacyWriteResult> {
  return pharmacyCall(`/v1/orders/pharmacy/orders/${encodeURIComponent(orderCaseId)}/tracking`, {
    method: 'POST',
    body,
  });
}

/**
 * GET /v1/orders/pharmacy/orders/:id/prescription → a short-lived signed link
 * to the WATERMARKED prescription PDF.
 *
 * RELEASED ONLY AFTER DISPENSING — server-enforced, and the console offers no
 * control that could call it earlier (see OrderDetailPanel).
 *
 * A GET, matching orders-service, because it reads a document — but it is NOT
 * free of consequence: every call writes an audit row naming the pharmacist who
 * took the document. That is why the console mints it behind an explicit
 * button press rather than on render, where a prefetch or a re-render would
 * put someone's name against a document they never opened.
 */
export function getPrescriptionDownload(orderCaseId: string): Promise<PrescriptionDownloadGrant> {
  return pharmacyCall(`/v1/orders/pharmacy/orders/${encodeURIComponent(orderCaseId)}/prescription`);
}

/**
 * Fetch the watermarked prescription PDF as a Blob (for inline viewing or print).
 */
export async function fetchPrescriptionPdfBlob(orderCaseId: string): Promise<Blob> {
  const token = getPharmacyToken();
  const path = `/v1/orders/pharmacy/orders/${encodeURIComponent(orderCaseId)}/prescription/file`;
  let response: Response;
  try {
    response = await fetch(`${GATEWAY_URL}${path}`, {
      headers: {
        accept: 'application/pdf',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    });
  } catch {
    throw new ApiError(0, `Gateway request failed on ${path}`);
  }
  if (response.status === 401) {
    notifyPharmacyUnauthorized();
    throw new ApiError(401, `Gateway request failed (401) on ${path}`);
  }
  if (!response.ok) {
    throw new ApiError(response.status, `Gateway request failed (${response.status}) on ${path}`);
  }
  const bytes = await response.arrayBuffer();
  return new Blob([bytes], { type: 'application/pdf' });
}

/**
 * Fetch the watermarked prescription PDF bytes and open it in a new tab.
 *
 * Uses the inline file route so local/stub environments work without GCS signed
 * URLs. Audits the download the same way the grant route does.
 */
export async function openPrescriptionPdf(orderCaseId: string): Promise<void> {
  const blob = await fetchPrescriptionPdfBlob(orderCaseId);
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 120_000);
}

/**
 * GET /v1/orders/pharmacy/activity → the SELF-SCOPED audit view for the signed-in
 * pharmacist (mirrors the clinician's MyActivityLog). Self-scoping is enforced
 * server-side from the verified principal — there is no actor parameter to send,
 * so this read cannot be widened from the browser.
 *
 * [BACKEND GAP] Not implemented on either service yet — see the module doc.
 */
export function getPharmacyActivity(): Promise<PharmacyActivityResponse> {
  return pharmacyCall('/v1/orders/pharmacy/activity');
}
